/**
 * The single way to change the language the app runs in.
 *
 * The language lives in several places: i18next (the UI strings), localStorage 'locale'
 * (the `locale` header Apollo sends, i.e. the language the server answers in, and the
 * language the next page load starts with), the chatbot slice in Redux, and users.locale
 * on the server. <html lang> follows i18next on its own (utils/documentLanguage).
 *
 * Every flow used to write its own subset of these. Switching accounts wrote none, saving
 * the profile wrote only the server, the footer selector wrote everything but the server,
 * and so on. App.js re-reads users.locale on every full page load, so each of those gaps
 * stayed invisible until the next reload, which then switched the language with no visible
 * cause. Going through this module is what keeps them together.
 */

import i18next from 'i18next';
import client from 'apollo';
import queries from 'apollo/queries';
import store from 'redux/store';
import { changeLocale } from 'redux/slices/chatbot';
import constants from 'config/constants';
import { normalizeLocale } from 'utils/localeHelper';
import { toUiLanguage } from './language';

export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, toUiLanguage } from './language';

const { AUTH_TOKEN } = constants;
const STORAGE_KEY = 'locale';

// A guess (browser language, IP lookup) must never replace a language the user chose or
// that came with their profile. Held in memory only: it matters within one page lifetime,
// which is exactly how long an IP lookup can stay in flight.
let explicitLanguageApplied = false;

const applyInMemory = (language) => {
  if (i18next.language !== language) i18next.changeLanguage(language);
  store.dispatch(changeLocale(normalizeLocale(language)));
};

/**
 * Applies a language everywhere the client keeps one. Idempotent.
 * @param {string|null|undefined} raw - any stored form of a language code
 * @param {{ source?: 'user'|'auto' }} [options] - 'auto' for guesses (browser, IP); a guess
 *   is ignored once a 'user' language has been applied in this page.
 * @returns {string|null} the language applied, or null when a guess was ignored
 */
export const applyLocale = (raw, { source = 'user' } = {}) => {
  if (source === 'auto' && explicitLanguageApplied) return null;
  if (source === 'user') explicitLanguageApplied = true;

  const language = toUiLanguage(raw);
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Storage unavailable: the language still applies for this page.
  }
  applyInMemory(language);
  return language;
};

/**
 * Applies the language saved on a user's profile. Every sign-in path calls this with the
 * user it receives, so a new session never keeps the previous one's language.
 * @param {{ locale?: string }|null|undefined} viewer
 * @returns {string|null}
 */
export const applyUserLocale = (viewer) => {
  if (!viewer?.locale) return null;
  return applyLocale(viewer.locale);
};

const signedInUserId = () => {
  try {
    if (!localStorage.getItem(AUTH_TOKEN)) return null;
    return localStorage.getItem('user_id');
  } catch {
    return null;
  }
};

/**
 * What every language selector calls.
 *
 * Signed in, the profile is saved first and the language applies only once the server has
 * accepted it: applying it before (or regardless of) the save is what made a failed save
 * look successful until the next reload put the old language back.
 * Signed out, there is no profile, so it applies locally.
 *
 * @param {string} language
 * @param {{ userId?: string|number|null }} [options] - defaults to the signed-in user
 * @returns {Promise<string>} the language applied
 * @throws when the profile could not be saved; nothing has changed in that case
 */
export const changeLanguagePreference = async (language, { userId } = {}) => {
  const target = toUiLanguage(language);
  const id = userId || signedInUserId();
  if (id) {
    await client.mutate({
      mutation: queries.editUser,
      variables: { user_id: id, locale: target },
    });
  }
  return applyLocale(target);
};

/**
 * Follows language changes made in other tabs. localStorage is shared, so without this a
 * tab kept rendering its old language while sending the new one as the `locale` header.
 *
 * Applies in memory only: writing storage again here would fire the event in the tab
 * that started the change, and the tabs would echo it back and forth.
 * @returns {() => void} unsubscribe
 */
export const followLocaleAcrossTabs = () => {
  if (typeof window === 'undefined') return () => {};
  const handler = (event) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    explicitLanguageApplied = true;
    applyInMemory(toUiLanguage(event.newValue));
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
};
